// Portfolio Administration v1.0 PR 6 — status tiles for the cross-portfolio
// Loan Tape page. Each tile is clickable to filter the table below.

import { cn } from "@/design-system/lib/utils";
import type { LoanTapeEntryDto } from "@/api/portfolioAdmin/pr6Types";

const STATUSES: { value: string; label: string; tone: string }[] = [
  { value: "open", label: "Not Started", tone: "bg-gray-mid/15 text-onyx" },
  { value: "entered", label: "Entered", tone: "bg-status-info/15 text-status-info" },
  { value: "variance-pending", label: "Variance", tone: "bg-status-warning/20 text-status-warning" },
  { value: "locked", label: "Locked", tone: "bg-status-success/15 text-status-success" },
  { value: "distributed", label: "Distributed", tone: "bg-gold/20 text-gold-shadow" },
];

interface LoanTapeStatusSummaryProps {
  entries: LoanTapeEntryDto[];
  selectedStatuses: string[];
  onToggleStatus: (status: string) => void;
}

function countByStatus(entries: LoanTapeEntryDto[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const e of entries) {
    counts[e.status] = (counts[e.status] ?? 0) + 1;
  }
  return counts;
}

export function LoanTapeStatusSummary({
  entries,
  selectedStatuses,
  onToggleStatus,
}: LoanTapeStatusSummaryProps) {
  const counts = countByStatus(entries);
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-5 gap-space-2"
      data-testid="loan-tape-status-summary"
    >
      {STATUSES.map((s) => {
        const isSelected = selectedStatuses.includes(s.value);
        return (
          <button
            key={s.value}
            type="button"
            onClick={() => onToggleStatus(s.value)}
            className={cn(
              "rounded-md p-space-2 border-[1.5px] text-left transition-colors",
              isSelected
                ? "border-gold bg-gold-highlight/10"
                : "border-border-light bg-white-pure hover:border-gold-shadow",
            )}
            data-testid={`loan-tape-status-tile-${s.value}`}
            aria-pressed={isSelected}
          >
            <p className={cn("text-caption uppercase tracking-[0.16em] m-0 px-[6px] py-[2px] rounded-sm inline-block", s.tone)}>
              {s.label}
            </p>
            <p className="font-display font-bold text-h2 m-0 mt-space-1">
              {counts[s.value] ?? 0}
            </p>
          </button>
        );
      })}
    </div>
  );
}

export const LOAN_TAPE_STATUS_OPTIONS = STATUSES;
